// How much a part swings, as a slider under the Kit choice (user request Oct
// 2026: "a slider underneath, so it could position the swing value relative to
// the amount of swing in the drum loop", then one per instrument). The slider
// shows the ratio that plays, straight to 7:1; its mark is where an offset of
// 0 sits (the kit for the band, the band for a part), and a drag near the mark
// snaps onto it. What it writes is the OFFSET from the mark, so a part keeps
// its distance from the kit when the loop changes (swingFeel.ts
// `kitSwingRatio`).
//
// Drawn by hand rather than in the browser's own look: the mark has to sit
// exactly under the thumb's centre at 0, which needs a thumb of known width.

import { useRef, type CSSProperties } from 'react';
import { swingRatioLabel } from '@/music/swingFeel';
import { swingSliderModel, swingSliderOffset } from './swingSlider';
import './SwingRatioSlider.css';

export function SwingRatioSlider({
  base,
  range,
  offset,
  onChange,
  label,
  testid,
}: {
  /** The ratio an offset of 0 plays: the kit's for the band, the band's for a part. */
  base: number;
  /** The ratios it spans (swingFeel.ts `swingRatioRange`). */
  range: readonly [number, number];
  /** The stored offset from `base`. */
  offset: number;
  onChange: (offset: number) => void;
  label: string;
  testid: string;
}) {
  const { min, max, steps, ratio } = swingSliderModel(base, range, offset);
  const dragging = useRef(false);
  const span = max - min || 1;
  const style = {
    '--swing-mark': `${-min / span}`,
    '--swing-fill': `${(steps - min) / span}`,
  } as CSSProperties;
  const text = swingRatioLabel(ratio);
  return (
    <span className="swing-slider" style={style}>
      <span className="swing-slider-rail">
        <span className="swing-slider-mark" aria-hidden="true" />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={steps}
          onPointerDown={() => {
            dragging.current = true;
          }}
          onPointerUp={() => {
            dragging.current = false;
          }}
          onPointerCancel={() => {
            dragging.current = false;
          }}
          onKeyDown={() => {
            dragging.current = false;
          }}
          onChange={(e) => onChange(swingSliderOffset(Number(e.target.value), dragging.current))}
          aria-label={label}
          aria-valuetext={text}
          data-testid={testid}
        />
      </span>
      <output>{text}</output>
    </span>
  );
}
